import { Button, Stack, Typography, useTheme } from "@mui/material";
import { MessageCircle } from "lucide-react";
import type { FC } from "react";
import { WhatIfCardViz } from "web_myplann/Goals/Components/WhatIf/WhatIfCardViz";
import { WhatIfExpandedCard } from "web_myplann/Goals/Components/WhatIf/WhatIfExpandedCard";
import { WhatIfOptionRow } from "web_myplann/Goals/Components/WhatIf/WhatIfOptionRow";
import { WhatIfSlider } from "web_myplann/Goals/Components/WhatIf/WhatIfSlider";
import { useWhatIf } from "web_myplann/Goals/Hooks/useWhatIf";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailWhatIf: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const plan = useWhatIf(goal);

	if (!plan.visible) return null;

	const active = plan.cardInfo[plan.activeCard];
	const collapsed = plan.cards.filter((card) => card !== plan.activeCard);

	return (
		<CollapsibleSection title={t(Resources.Goals.Detail.WhatIf.title)}>
			<Stack
				bgcolor={theme.palette.background.paper}
				px={1.6}
				py={1.6}
				gap={1.2}
			>
				<WhatIfSlider
					value={plan.extraAmount}
					onChange={plan.setExtraAmount}
					currencyCode={goal.currencyCode}
				/>

				{plan.extraAmount > 0 ? (
					<Stack gap={0.6}>
						<WhatIfExpandedCard accent={active.accent} label={active.label}>
							<WhatIfCardViz
								card={plan.activeCard}
								plan={plan}
								currencyCode={goal.currencyCode}
							/>
						</WhatIfExpandedCard>

						{collapsed.length > 0 && (
							<>
								<Typography
									variant="caption"
									fontWeight={600}
									color={theme.palette.grey[400]}
									textTransform="uppercase"
									letterSpacing={0.5}
									mt={0.6}
								>
									{t(Resources.Goals.Detail.WhatIf.otherOptions)}
								</Typography>
								{collapsed.map((card) => {
									const info = plan.cardInfo[card];
									const Icon = info.icon;
									return (
										<WhatIfOptionRow
											key={card}
											icon={<Icon size={16} color={info.accent} />}
											accent={info.accent}
											label={info.label}
											subtitle={info.subtitle}
											onClick={() => plan.setActiveCard(card)}
										/>
									);
								})}
							</>
						)}

						<Button
							variant="primary"
							startIcon={<MessageCircle size={16} />}
							onClick={plan.openChat}
							sx={{ mt: 1 }}
						>
							{t(Resources.Goals.Detail.WhatIf.talkToAdvisor)}
						</Button>
					</Stack>
				) : (
					<Typography
						variant="caption"
						color={theme.palette.grey[400]}
						textAlign="center"
						py={2}
					>
						{t(Resources.Goals.Detail.WhatIf.placeholder)}
					</Typography>
				)}
			</Stack>
		</CollapsibleSection>
	);
};
